import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { Loader2, Pause, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils/cn';
import type { CampaignPromptRow } from '../api';

/* ================================================================
 *  Global audio coordinator — only ONE prompt plays at a time.
 *  Uses requestAnimationFrame for smooth progress.
 * ================================================================ */

interface PlayState {
  activeKey: string | null;
  pct: number;
}

let state: PlayState = { activeKey: null, pct: 0 };
let gAudio: HTMLAudioElement | null = null;
let rafId: number | null = null;
const subs = new Set<() => void>();

function emit() { subs.forEach((fn) => fn()); }
function subscribe(cb: () => void) { subs.add(cb); return () => { subs.delete(cb); }; }
function getSnapshot(): PlayState { return state; }

function tick() {
  if (!gAudio || gAudio.paused) { rafId = null; return; }
  const dur = gAudio.duration;
  if (Number.isFinite(dur) && dur > 0) {
    state = { ...state, pct: Math.min(100, (gAudio.currentTime / dur) * 100) };
    emit();
  }
  rafId = requestAnimationFrame(tick);
}

function stopAnim() {
  if (rafId !== null) { cancelAnimationFrame(rafId); rafId = null; }
}

function stopAll() {
  stopAnim();
  if (gAudio) { gAudio.pause(); gAudio.src = ''; gAudio = null; }
  state = { activeKey: null, pct: 0 };
  emit();
}

function playUrl(key: string, url: string) {
  stopAll();
  const a = new Audio(url);
  a.addEventListener('ended', () => {
    stopAnim();
    state = { activeKey: key, pct: 100 };
    emit();
    setTimeout(() => {
      if (state.activeKey === key) {
        state = { activeKey: null, pct: 0 };
        gAudio = null;
        emit();
      }
    }, 500);
  });
  a.addEventListener('error', () => stopAll());
  gAudio = a;
  state = { activeKey: key, pct: 0 };
  emit();
  void a.play().then(() => { if (!rafId) rafId = requestAnimationFrame(tick); });
}

/* ================================================================
 *  Prompt row inside the modal
 * ================================================================ */

function categoryLabel(p: CampaignPromptRow): string {
  const cat = (p.promptCategory || p.promptType || '').toLowerCase();
  const map: Record<string, string> = {
    welcome: 'Welcome',
    menu: 'Menu',
    noinput: 'No Input',
    no_input: 'No Input',
    wronginput: 'Wrong Input',
    wrong_input: 'Wrong Input',
    thanks: 'Thanks',
    no_agent: 'No Agent',
    noagent: 'No Agent',
  };
  return map[cat] || p.promptCategory || p.promptType || 'Prompt';
}

function PromptRow({ prompt, campaignId }: { prompt: CampaignPromptRow; campaignId: number }) {
  const ps = useSyncExternalStore(subscribe, getSnapshot);
  const key = `${campaignId}-${prompt.promptId}`;
  const playing = ps.activeKey === key;
  const url = prompt.promptUrl;

  const handleClick = () => {
    if (playing) { stopAll(); return; }
    if (url) playUrl(key, url);
  };

  const pct = playing && ps.pct > 0 ? ps.pct : 0;

  return (
    <div className="relative flex items-center gap-3 rounded-lg border border-border px-3 py-2.5">
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className={cn(
          'h-8 w-8 shrink-0 rounded-full',
          playing && 'bg-primary text-primary-foreground hover:bg-primary/90',
        )}
        onClick={handleClick}
        disabled={!url}
        title={!url ? 'No audio file' : playing ? 'Pause' : 'Play'}
      >
        {playing ? (
          <Pause className="h-4 w-4" />
        ) : (
          <Play className="h-4 w-4" />
        )}
      </Button>

            <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-foreground">
          {categoryLabel(prompt)}
        </p>
        <p className="text-xs text-muted-foreground truncate">
          {prompt.fileName || prompt.promptName || `Prompt #${prompt.promptId}`}
          {prompt.promptDuration ? ` · ${prompt.promptDuration}s` : ''}
        </p>
      </div>

      {/* Progress bar */}
      {playing && pct > 0 && (
        <div className="absolute bottom-0 left-0 right-0 h-[2px] overflow-hidden rounded-b-lg">
          <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
        </div>
      )}
    </div>
  );
}

/* ================================================================
 *  Main component — play button + modal
 * ================================================================ */

/* Prompt URL cache by campaignId */
const promptCache = new Map<number, CampaignPromptRow[]>();

interface Props {
  campaignId: number;
}

export function CampaignPlayButton({ campaignId }: Props) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [prompts, setPrompts] = useState<CampaignPromptRow[]>([]);

  const btnRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const tr = btnRef.current?.closest('tr');
    if (tr) tr.style.position = 'relative';
  }, []);

  // Track playing state for the row progress bar
  const ps = useSyncExternalStore(subscribe, getSnapshot);
  const isThisCampaignPlaying = ps.activeKey?.startsWith(`${campaignId}-`) ?? false;

  const handleClick = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    // Check cache first
    const cached = promptCache.get(campaignId);
    if (cached) {
      setPrompts(cached);
      setOpen(true);
      return;
    }

    // Fetch campaign info
    setLoading(true);
    try {
      const { apiClient } = await import('@/lib/api/client');
      const resp = await apiClient.get(`/api/obd/campaign/info/${campaignId}`);
      const data = resp.data;
      const list: CampaignPromptRow[] = data?.campaignPrompts ?? [];
      promptCache.set(campaignId, list);
      setPrompts(list);
      setOpen(true);
    } catch { /* ignore */ }
    setLoading(false);
  }, [campaignId]);

  const handleClose = () => {
    stopAll();
    setOpen(false);
  };

  return (
    <>
      <Button
        ref={btnRef}
        type="button"
        variant="ghost"
        size="icon"
        className={cn(
          'h-7 w-7 shrink-0 rounded-full',
          isThisCampaignPlaying && 'bg-primary text-primary-foreground hover:bg-primary/90',
        )}
        onClick={handleClick}
        disabled={loading}
        title="Play IVR"
      >
        {loading ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : isThisCampaignPlaying ? (
          <Pause className="h-3.5 w-3.5" />
        ) : (
          <Play className="h-3.5 w-3.5" />
        )}
      </Button>

      {/* Row progress bar */}
      {isThisCampaignPlaying && ps.pct > 0 && (
        <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-px overflow-hidden">
          <div className="h-full bg-primary" style={{ width: `${ps.pct}%` }} />
        </div>
      )}

      {/* Prompts modal */}
      <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Campaign Voice Files</DialogTitle>
          </DialogHeader>

          {prompts.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              No voice files found for this campaign.
            </p>
          ) : (
            <div className="flex flex-col gap-2 max-h-[60vh] overflow-y-auto py-2">
              {prompts.map((p, i) => (
                <PromptRow
                  key={p.promptId ?? i}
                  prompt={p}
                  campaignId={campaignId}
                />
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}